import { useState } from 'react';
import { Chip, Link, Icon } from '@nimbus-ds/components';
import { EditIcon, PlusCircleIcon } from '@nimbus-ds/icons';
import { useTranslation } from 'react-i18next';
import { Domain } from '@tiendanube/common/src/enums';
import ActionableToast from 'commons/components/ActionableToast';
import Stack from 'commons/components/Stack';

interface Category {
  id: string;
  title: string;
  isAiGenerated?: boolean;
}

interface CategoriesDisplayProps {
  selectedCategories: string[];
  allCategories: Category[];
  onRemoveCategory: (categoryId: string) => void;
  onEditCategories: () => void;
  feedbackMessage?: string;
  thumbsUpAriaLabel?: string;
  thumbsDownAriaLabel?: string;
  onPositiveFeedback?: () => void;
  onNegativeFeedback?: () => void;
  remainingAiUses?: number;
}

function CategoriesDisplay({
  selectedCategories,
  allCategories,
  onRemoveCategory,
  onEditCategories,
  feedbackMessage,
  thumbsUpAriaLabel,
  thumbsDownAriaLabel,
  onPositiveFeedback,
  onNegativeFeedback,
  remainingAiUses,
}: Readonly<CategoriesDisplayProps>): JSX.Element {
  const { t } = useTranslation(Domain.CATALOG);
  const hasCategories = selectedCategories.length > 0;
  const [isFeedbackDismissed, setIsFeedbackDismissed] = useState(false);

  const shouldShowFeedback =
    !isFeedbackDismissed &&
    feedbackMessage &&
    onPositiveFeedback &&
    onNegativeFeedback;

  return (
    <Stack column spacing="base" align="stretch">
      {hasCategories && (
        <Stack spacing="tight" wrap>
          {allCategories
            .filter((category) => selectedCategories.includes(category.id))
            .map((category) => (
              <Chip
                key={category.id}
                text={category.title}
                removable
                aiGenerated={category.isAiGenerated}
                onClick={() => onRemoveCategory(category.id)}
              />
            ))}
        </Stack>
      )}

      {!!shouldShowFeedback && (
        <ActionableToast
          message={feedbackMessage}
          thumbsUpAriaLabel={thumbsUpAriaLabel}
          thumbsDownAriaLabel={thumbsDownAriaLabel}
          onPositiveFeedback={onPositiveFeedback}
          onNegativeFeedback={onNegativeFeedback}
          remainingAiUses={remainingAiUses}
          onClose={() => setIsFeedbackDismissed(true)}
        />
      )}

      <Link
        as="button"
        type="button"
        appearance="primary"
        onClick={onEditCategories}
        textDecoration="none"
      >
        <Icon
          source={hasCategories ? <EditIcon /> : <PlusCircleIcon />}
          color="currentColor"
        />
        {hasCategories
          ? t('products.detail.editCategories')
          : t('products.detail.addCategories')}
      </Link>
    </Stack>
  );
}

export default CategoriesDisplay;
